import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, Legend,
} from 'recharts';
import {
  Loader2, FlaskConical, Send, Eye, Play, PhoneIncoming, CalendarClock, ThumbsUp, ThumbsDown,
  Clock, Crown, RefreshCw, Video, Mic, ImageOff, Shuffle, QrCode, ChevronRight, TrendingUp, PenLine, Gauge,
  Check, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { AbTestsSection } from '../components/AbTestsSection';

const POLL_MS = 30000;
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

const MODE_META = {
  video: { label: 'Video', icon: Video, color: '#38bdf8', text: 'text-sky-400', border: 'border-sky-500/30' },
  audio: { label: 'Voice', icon: Mic, color: '#34d399', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  none: { label: 'No media', icon: ImageOff, color: '#94a3b8', text: 'text-slate-400', border: 'border-slate-500/30' },
  free: { label: 'Free', icon: Shuffle, color: '#a78bfa', text: 'text-violet-400', border: 'border-violet-500/30' },
};
const modeMeta = (m) => MODE_META[m] || MODE_META.free;

const FALLBACK_COLORS = ['#a78bfa', '#fb923c', '#34d399', '#f472b6', '#facc15', '#94a3b8'];
const variantColors = (variants) => {
  const used = new Set();
  const map = {};
  variants.forEach(v => {
    let color = modeMeta(v.media_mode).color;
    if (used.has(color)) color = FALLBACK_COLORS.find(c => !used.has(c)) || color;
    used.add(color);
    map[v.id] = color;
  });
  return map;
};

const EVENT_META = {
  sent: { label: 'Letter sent', icon: Send, cls: 'text-slate-400' },
  scan: { label: 'QR scan', icon: QrCode, cls: 'text-sky-400' },
  callback: { label: 'Callback request', icon: PhoneIncoming, cls: 'text-amber-400' },
  appointment: { label: 'Appointment booked', icon: CalendarClock, cls: 'text-amber-400' },
  lead: { label: 'Response', icon: PhoneIncoming, cls: 'text-amber-400' },
  followup_interested: { label: 'Phone: interested', icon: ThumbsUp, cls: 'text-emerald-400' },
  followup_no_interest: { label: 'Phone: no interest', icon: ThumbsDown, cls: 'text-red-400' },
  followup_later: { label: 'Phone: try later', icon: Clock, cls: 'text-amber-400' },
};

const relTime = (iso) => {
  if (!iso) return '—';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function EngagementSigPanel({ es }) {
  if (!es || (es.results.length === 0 && (es.progress || []).every(p => !p.sessions))) return null;
  const modeWord = (mode, pct) => mode === 'audio' ? `voice ⌀ ${pct}% listened` : `video ⌀ ${pct}% watched`;
  const fmtSec = (s) => s >= 60 ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')} min` : `${Math.round(s)}s`;
  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="ab-live-eng-sig">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1.5">
        <Gauge className="w-3.5 h-3.5" /> Engagement significance
      </p>
      {es.results.length === 0 ? (
        <div className="space-y-1">
          <p className="text-[11px] text-muted-foreground">Unlocks at {es.min_n} engaged sessions per variant:</p>
          {(es.progress || []).map(p => (
            <p key={p.variant} className="text-[10px] font-mono text-muted-foreground/80">
              {p.variant}: {(p.mode === 'audio' || p.mode === 'video') ? `${p.media_sessions}/${es.min_n} media sessions · ` : ''}{p.sessions}/{es.min_n} sessions
            </p>
          ))}
        </div>
      ) : (
        <div className="space-y-1.5">
          {es.results.map((r, i) => (
            <p key={i} className={`text-[11px] flex items-center gap-1.5 ${r.significant ? 'text-emerald-400' : 'text-muted-foreground'}`}
              data-testid={`ab-live-eng-sig-${r.metric}-${i}`}>
              {r.significant && <Crown className="w-3 h-3 shrink-0" />}
              {r.metric === 'media_completion'
                ? `${r.winner} (${modeWord(r.winner_mode, r.winner_mean)}) beats ${r.loser} (${modeWord(r.loser_mode, r.loser_mean)}) — ${r.confidence}% confidence`
                : `${r.winner} holds attention longer than ${r.loser} (⌀ ${fmtSec(r.winner_mean)} vs ${fmtSec(r.loser_mean)} on page) — ${r.confidence}% confidence`}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function VariantCard({ variant, targetN, leaders }) {
  const s = variant.stats || {};
  const meta = modeMeta(variant.media_mode);
  const Icon = meta.icon;
  const assignedPct = Math.min(100, pct(s.pages || 0, targetN));
  const scanRate = pct(s.scanned || 0, s.sent || 0);
  const respRate = s.sent > 0 ? pct(s.responded || 0, s.sent) : null;
  const isScanLeader = leaders.scan === variant.id;
  const isRespLeader = leaders.resp === variant.id;

  const rows = [
    { icon: Send, label: 'Sent', value: s.sent || 0 },
    { icon: Eye, label: 'Scanned', value: s.scanned || 0, cls: s.scanned > 0 ? 'text-sky-400' : '' },
    { icon: Play, label: 'Played', value: s.played || 0, cls: s.played > 0 ? 'text-sky-400' : '' },
    { icon: PhoneIncoming, label: 'Responded', value: s.responded || 0, cls: s.responded > 0 ? 'text-emerald-400' : '' },
    { icon: ThumbsDown, label: 'No interest', value: s.no_interest || 0, cls: s.no_interest > 0 ? 'text-red-400/80' : '' },
    { icon: Clock, label: 'Open', value: s.open || 0 },
  ];

  return (
    <div className={`bg-card border rounded-sm p-4 flex flex-col gap-3 ${meta.border}`} data-testid={`ab-live-variant-${variant.id}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${meta.text}`} />
        <span className="text-sm font-medium text-foreground truncate">{variant.name}</span>
        <span className={`text-[9px] uppercase tracking-wider ${meta.text}`}>{meta.label}</span>
        {(isScanLeader || isRespLeader) && (
          <span className="ml-auto inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-sm px-1.5 py-0.5"
            data-testid={`ab-live-leader-${variant.id}`} title={`Leading in ${[isScanLeader && 'scan rate', isRespLeader && 'response rate'].filter(Boolean).join(' + ')}`}>
            <Crown className="w-3 h-3" /> Leader
          </span>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
          <span className="uppercase tracking-wider">Letters assigned</span>
          <span className="font-mono" data-testid={`ab-live-counter-${variant.id}`}>{s.pages || 0}/{targetN}</span>
        </div>
        <div className="h-2 bg-secondary/40 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${assignedPct}%`, background: meta.color }} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-x-3 gap-y-2">
        {rows.map(r => (
          <div key={r.label} className="flex items-center gap-1.5">
            <r.icon className="w-3 h-3 text-muted-foreground/60 shrink-0" />
            <span className="text-[10px] text-muted-foreground">{r.label}</span>
            <span className={`ml-auto text-xs font-mono ${r.cls || 'text-foreground'}`}>{r.value}</span>
          </div>
        ))}
      </div>
      {variant.handwritten_split && (
        <div className="border-t border-border/60 pt-2 space-y-1" data-testid={`ab-live-handwritten-${variant.id}`}>
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
            <PenLine className="w-3 h-3 text-amber-400" /> Handwritten vs. printed
          </p>
          {[['Handwritten', variant.handwritten_split.handwritten, 'text-amber-300'],
            ['Printed', variant.handwritten_split.printed, 'text-muted-foreground']].map(([label, s2, cls]) => (
            <div key={label} className="flex items-center gap-2 text-[10px]">
              <span className={`w-20 shrink-0 ${cls}`}>{label}</span>
              <span className="font-mono text-foreground">{s2.sent ?? s2.pages} sent</span>
              <span className="text-muted-foreground/60">·</span>
              <span className="font-mono text-sky-400">{s2.scanned} scanned ({pct(s2.scanned, s2.sent ?? s2.pages)}%)</span>
              <span className="text-muted-foreground/60">·</span>
              <span className="font-mono text-emerald-400">{s2.responded} resp.</span>
            </div>
          ))}
          {(() => {
            const sp = variant.handwritten_split;
            const sig = sp.significance;
            if (!sig) return null;
            const minN = sig.min_n ?? 25;
            if (!(sig.results || []).length) return (
              <p className="text-[9px] text-muted-foreground/70 pt-0.5" data-testid={`ab-live-hw-sig-hint-${variant.id}`}>
                Significance unlocks at {minN} sent letters per version — handwritten {sp.handwritten.sent ?? sp.handwritten.pages}/{minN} · printed {sp.printed.sent ?? sp.printed.pages}/{minN}
              </p>
            );
            return sig.results.map(r => (
              <div key={r.metric} data-testid={`ab-live-hw-sig-${variant.id}-${r.metric}`}
                className={`flex items-center gap-1 text-[10px] pt-0.5 ${r.significant ? 'text-emerald-400' : 'text-muted-foreground/70'}`}>
                {r.significant && <Crown className="w-3 h-3 shrink-0" />}
                {r.winner === null
                  ? `No difference in ${r.metric === 'scan_rate' ? 'scan rate' : 'response rate'}`
                  : `${r.winner === 'handwritten' ? 'Handwritten' : 'Printed'} ahead — ${r.confidence}% confidence (${r.metric === 'scan_rate' ? 'scan rate' : 'response rate'})`}
              </div>
            ));
          })()}
        </div>
      )}
      {variant.engagement && variant.engagement.sessions > 0 && (
        <div className="border-t border-border/60 pt-2" data-testid={`ab-live-engagement-${variant.id}`}>
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1">Engagement</p>
          <p className="text-[10px] font-mono text-foreground">
            ⌀ {Math.floor((variant.engagement.avg_seconds_on_page || 0) / 60)}:{String(Math.round((variant.engagement.avg_seconds_on_page || 0) % 60)).padStart(2, '0')} min on page
            {variant.engagement.avg_video_pct != null ? ` · video ⌀ ${variant.engagement.avg_video_pct}%` : ''}
            {variant.engagement.avg_audio_pct != null ? ` · voice ⌀ ${variant.engagement.avg_audio_pct}%` : ''}
            {(variant.engagement.phone_clicks + variant.engagement.email_clicks + variant.engagement.calendar_opens) > 0
              ? ` · ${variant.engagement.phone_clicks + variant.engagement.email_clicks + variant.engagement.calendar_opens} contact tap(s)` : ''}
          </p>
        </div>
      )}
      <div className="flex items-center gap-3 pt-2 border-t border-border/60">
        <div className="flex-1">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Scan rate</p>
          <p className={`text-lg font-heading ${isScanLeader ? 'text-amber-400' : 'text-foreground'}`}>{scanRate}%</p>
        </div>
        <div className="flex-1">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Response rate</p>
          <p className={`text-lg font-heading ${isRespLeader ? 'text-amber-400' : 'text-foreground'}`}>{respRate === null ? '—' : `${respRate}%`}</p>
          <p className="text-[9px] text-muted-foreground/60">of {s.sent || 0} sent</p>
        </div>
      </div>
    </div>
  );
}

function SignificancePanel({ sig, variants }) {
  const results = sig?.results || [];
  const significant = results.filter(r => r.significant);
  const best = results.length ? Math.max(...results.map(r => r.confidence)) : null;
  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="ab-live-significance">
      <h2 className="text-sm font-medium text-foreground mb-2 inline-flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-violet-400" /> Live evaluation
      </h2>
      {significant.length > 0 ? significant.map((r, i) => (
        <div key={i} className="flex items-center gap-2 text-[11px] text-emerald-400 bg-emerald-950/20 border border-emerald-900/40 rounded-sm px-3 py-2 mb-2">
          <Crown className="w-3.5 h-3.5 shrink-0" />
          <span><span className="font-medium">"{r.winner}"</span> beats <span className="font-medium">"{r.loser}"</span> — {r.confidence}% confidence ({r.metric === 'scan_rate' ? 'scan rate' : 'response rate'})</span>
        </div>
      )) : results.length > 0 ? (
        <p className="text-[11px] text-muted-foreground bg-secondary/30 border border-border/60 rounded-sm px-3 py-2 mb-2">
          No statistically significant winner yet — best signal {best?.toFixed(1)}% (needs ≥95%). Keep collecting data.
        </p>
      ) : (
        <p className="text-[11px] text-muted-foreground bg-secondary/30 border border-border/60 rounded-sm px-3 py-2 mb-2">
          Significance unlocks at {sig?.min_n ?? 25} sent letters per variant — counters below show the progress.
        </p>
      )}
      <div className="space-y-1.5 mt-2">
        {(variants || []).map(v => {
          const n = v.stats?.sent || 0;
          const minN = sig?.min_n ?? 25;
          return (
            <div key={v.id} className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground w-28 truncate">{v.name}</span>
              <div className="flex-1 h-1.5 bg-border/40 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${n >= minN ? 'bg-emerald-500/70' : 'bg-violet-500/60'}`}
                  style={{ width: `${Math.min(100, Math.round((n / minN) * 100))}%` }} />
              </div>
              <span className={`text-[10px] font-mono w-12 text-right ${n >= minN ? 'text-emerald-400' : 'text-muted-foreground'}`}>{n}/{minN}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HeadlineSplitPanel({ hs }) {
  if (!hs) return null;
  const groups = [
    ['with_headline', 'Mit Überschrift', hs.with_headline, 'text-amber-300'],
    ['without_headline', 'Ohne Überschrift', hs.without_headline, 'text-sky-300'],
  ];
  const total = (hs.with_headline?.pages || 0) + (hs.without_headline?.pages || 0);
  const results = hs.significance?.results || [];
  const significant = results.filter(r => r.significant);
  const minN = hs.significance?.min_n ?? 25;
  const nameOf = (k) => (k === 'with_headline' ? 'Mit Überschrift' : 'Ohne Überschrift');
  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="ab-live-headline-split">
      <h2 className="text-sm font-medium text-foreground mb-1 inline-flex items-center gap-2">
        <PenLine className="w-4 h-4 text-amber-400" /> Überschrift-Vergleich
      </h2>
      <p className="text-[10px] text-muted-foreground mb-2">
        Briefe mit vs. ohne handgeschriebene Überschrift („Haben Sie schon einmal…") — über alle Varianten hinweg.
      </p>
      {total === 0 || (hs.with_headline?.pages || 0) === 0 || (hs.without_headline?.pages || 0) === 0 ? (
        <p className="text-[11px] text-muted-foreground bg-secondary/30 border border-border/60 rounded-sm px-3 py-2">
          Noch keine Vergleichsdaten — es braucht versendete Briefe in beiden Gruppen (mit UND ohne Überschrift).
          Den Schalter findest du am Brief bzw. in der Druckvorschau.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            {groups.map(([key, label, g, cls]) => (
              <div key={key} className="border border-border/60 bg-secondary/20 rounded-sm p-2.5" data-testid={`ab-live-headline-${key}`}>
                <p className={`text-[11px] font-medium ${cls}`}>{label}</p>
                <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                  {g?.sent || 0} sent · {g?.scanned || 0} scans ({pct(g?.scanned || 0, g?.sent || 0)}%)
                </p>
                <p className="text-[10px] text-muted-foreground font-mono">
                  {g?.responded || 0} responses ({pct(g?.responded || 0, g?.sent || 0)}%)
                </p>
              </div>
            ))}
          </div>
          <div className="mt-2">
            {significant.length > 0 ? significant.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] text-emerald-400 bg-emerald-950/20 border border-emerald-900/40 rounded-sm px-3 py-2 mb-1"
                data-testid={`ab-live-headline-sig-${r.metric}`}>
                <Crown className="w-3.5 h-3.5 shrink-0" />
                <span><span className="font-medium">{nameOf(r.winner)}</span> gewinnt — {r.confidence}% Konfidenz ({r.metric === 'scan_rate' ? 'Scan-Rate' : 'Antwort-Rate'})</span>
              </div>
            )) : results.length > 0 ? (
              <p className="text-[10px] text-muted-foreground">Noch kein signifikanter Gewinner (braucht ≥95% Konfidenz) — weiter Daten sammeln.</p>
            ) : (
              <p className="text-[10px] text-muted-foreground">Signifikanz ab {minN} versendeten Briefen pro Gruppe.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function LiveTicker({ events, isAdmin, onExcludeScan }) {
  return (
    <div className="bg-card border border-border rounded-sm" data-testid="ab-live-ticker">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <h2 className="text-sm font-medium text-foreground">Live ticker</h2>
        <span className="text-[10px] text-muted-foreground">latest scans, responses & sends</span>
      </div>
      {(events || []).length === 0 ? (
        <p className="text-xs text-muted-foreground p-5">No events yet — they appear here the moment a letter is sent, a QR code is scanned or an owner responds.</p>
      ) : (
        <div className="divide-y divide-border/40 max-h-[420px] overflow-y-auto">
          {events.map((e, i) => {
            const meta = EVENT_META[e.type] || EVENT_META.lead;
            return (
              <div key={i} className="px-4 py-2.5 flex items-center gap-3 group" data-testid={`ab-live-event-${i}`}>
                <meta.icon className={`w-3.5 h-3.5 shrink-0 ${meta.cls}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-foreground truncate">
                    <span className={`font-medium ${meta.cls}`}>{meta.label}</span>
                    {e.company_name && <span> — {e.company_name}</span>}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">{e.variant_name || 'Unassigned'}</p>
                </div>
                <span className="text-[10px] font-mono text-muted-foreground shrink-0" title={e.at}>{relTime(e.at)}</span>
                {isAdmin && e.type === 'scan' && e.visit_id && (
                  <button onClick={() => onExcludeScan(e)} title="Exclude this scan from all statistics (e.g. accidental team scan)"
                    data-testid={`ab-live-exclude-${i}`}
                    className="shrink-0 p-1 rounded border border-transparent text-muted-foreground/30 hover:text-red-400 hover:border-red-500/40 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:text-red-400 transition-all">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AbTestLivePage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [experiments, setExperiments] = useState([]);
  const [unassigned, setUnassigned] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [metric, setMetric] = useState('scans');
  const [editN, setEditN] = useState(false);
  const [nDraft, setNDraft] = useState(50);
  const [editW, setEditW] = useState(false);
  const [wDraft, setWDraft] = useState(45);
  const timerRef = useRef(null);

  const loadExperiments = useCallback(() => {
    return api.getAbExperiments().then(r => {
      const exps = r.data.experiments || [];
      setExperiments(exps);
      setUnassigned(r.data.unassigned_pages || 0);
      return exps;
    });
  }, []);

  useEffect(() => {
    loadExperiments().then(exps => {
      const active = exps.find(e => e.status === 'active') || exps[0];
      if (active) setSelectedId(active.id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, [loadExperiments]);

  const loadLive = useCallback((id, silent = false) => {
    if (!id) return;
    if (!silent) setRefreshing(true);
    api.getAbLive(id)
      .then(r => { setLive(r.data); setLastUpdate(new Date()); })
      .catch(() => {})
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    loadLive(selectedId);
    timerRef.current = setInterval(() => loadLive(selectedId, true), POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [selectedId, loadLive]);

  const reloadAll = useCallback(() => {
    loadExperiments().then(exps => {
      if (!selectedId) {
        const active = exps.find(e => e.status === 'active') || exps[0];
        if (active) { setSelectedId(active.id); return; }
      }
      if (selectedId && !exps.some(e => e.id === selectedId)) {
        const active = exps.find(e => e.status === 'active') || exps[0];
        setSelectedId(active ? active.id : null);
        if (!active) setLive(null);
        return;
      }
      if (selectedId) loadLive(selectedId, true);
    }).catch(() => {});
  }, [loadExperiments, selectedId, loadLive]);

  const handleExcludeScan = async (e) => {
    if (!window.confirm(`Exclude this QR scan from all statistics?\n\n${e.company_name || ''} — ${new Date(e.at).toLocaleString('en-GB')}\n\nUse this for accidental scans by the team. Cannot be undone.`)) return;
    try {
      await api.excludeLandingVisit(e.visit_id);
      toast.success('Scan excluded from statistics');
      loadLive(selectedId, true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Exclude failed');
    }
  };

  const saveN = () => {
    const n = parseInt(nDraft, 10);
    if (!n || n < 1 || n > 1000) { toast.error('Target must be between 1 and 1000'); return; }
    api.updateAbExperiment(selectedId, { target_n: n })
      .then(() => { toast.success(`Target set to n=${n} per variant`); setEditN(false); reloadAll(); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to update target'));
  };

  const saveW = () => {
    const w = parseInt(wDraft, 10);
    if (!w || w < 7 || w > 365) { toast.error('Antwortfenster: 7–365 Tage'); return; }
    api.updateAbExperiment(selectedId, { window_days: w })
      .then(() => { toast.success(`Antwortfenster auf ${w} Tage gesetzt`); setEditW(false); reloadAll(); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed'));
  };

  const archiveExp = () => {
    api.updateAbExperiment(selectedId, { status: 'archived' })
      .then(() => { toast.success('Test archiviert'); reloadAll(); })
      .catch(() => toast.error('Archivieren fehlgeschlagen'));
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 text-violet-500 animate-spin" /></div>;

  if (!selectedId || !live) {
    return (
      <div className="space-y-5 animate-fade-in-up" data-testid="ab-live-page">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
            <FlaskConical className="w-7 h-7 text-violet-400" /> A/B Testing
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Compare video, voice and plain landing pages — live counters, evaluation and setup in one place</p>
        </div>
        <AbTestsSection experiments={experiments} unassigned={unassigned} reload={reloadAll} />
      </div>
    );
  }

  const exp = live.experiment || {};
  const variants = live.variants || [];
  const totalAssigned = variants.reduce((a, v) => a + (v.stats?.pages || 0), 0);
  const totalTarget = (exp.target_n || 50) * variants.length;

  // Leaders (need at least 1 relevant data point)
  const leaders = { scan: null, resp: null };
  let bestScan = 0; let bestResp = 0;
  variants.forEach(v => {
    const s = v.stats || {};
    const sr = s.sent > 0 ? s.scanned / s.sent : 0;
    const rr = s.sent > 0 ? s.responded / s.sent : 0;
    if (s.scanned > 0 && sr > bestScan) { bestScan = sr; leaders.scan = v.id; }
    if (s.responded > 0 && rr > bestResp) { bestResp = rr; leaders.resp = v.id; }
  });

  const chartData = (live.timeline || []).map(row => ({ ...row, label: row.day.slice(8) + '.' + row.day.slice(5, 7) }));
  const prefix = metric === 'scans' ? 'scans_' : 'resp_';

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="ab-live-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
            <FlaskConical className="w-7 h-7 text-violet-400" /> A/B Testing
          </h1>
          <p className="text-muted-foreground text-sm mt-1 flex items-center gap-1.5 flex-wrap">
            <span>{exp.name} — {totalAssigned}/{totalTarget} letters assigned · target n={exp.target_n || 50} per variant</span>
            {editN ? (
              <span className="inline-flex items-center gap-1">
                <input type="number" min={1} max={1000} value={nDraft} autoFocus
                  onChange={e => setNDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') setEditN(false);
                    if (e.key === 'Enter') saveN();
                  }}
                  data-testid="ab-live-edit-n-input"
                  className="w-16 bg-secondary border border-violet-700 rounded-sm px-1.5 py-0.5 text-xs text-foreground focus:outline-none" />
                <button data-testid="ab-live-edit-n-save" onClick={saveN}
                  className="text-emerald-400 hover:text-emerald-300 transition-colors"><Check className="w-3.5 h-3.5" /></button>
                <button onClick={() => setEditN(false)}
                  className="text-muted-foreground/60 hover:text-foreground transition-colors"><X className="w-3.5 h-3.5" /></button>
              </span>
            ) : (
              <button onClick={() => { setNDraft(exp.target_n || 50); setEditN(true); }}
                title="Change target n per variant" data-testid="ab-live-edit-n"
                className="text-muted-foreground/50 hover:text-violet-400 transition-colors">
                <PenLine className="w-3.5 h-3.5" />
              </button>
            )}
            <span className="text-muted-foreground/50">·</span>
            {editW ? (
              <span className="inline-flex items-center gap-1">
                <input type="number" min={7} max={365} value={wDraft} autoFocus
                  onChange={e => setWDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') setEditW(false);
                    if (e.key === 'Enter') saveW();
                  }}
                  data-testid="ab-live-edit-w-input"
                  className="w-16 bg-secondary border border-violet-700 rounded-sm px-1.5 py-0.5 text-xs text-foreground focus:outline-none" />
                <button data-testid="ab-live-edit-w-save" onClick={saveW}
                  className="text-emerald-400 hover:text-emerald-300 transition-colors"><Check className="w-3.5 h-3.5" /></button>
                <button onClick={() => setEditW(false)}
                  className="text-muted-foreground/60 hover:text-foreground transition-colors"><X className="w-3.5 h-3.5" /></button>
              </span>
            ) : (
              <button onClick={() => { setWDraft(exp.window_days || 45); setEditW(true); }}
                title="Antwortfenster ändern: So viele Tage nach Versand wird auf eine Reaktion gewartet, bevor ein Brief als 'keine Antwort' zählt"
                data-testid="ab-live-edit-w"
                className="inline-flex items-center gap-1 text-muted-foreground/70 hover:text-violet-400 transition-colors">
                Antwortfenster {exp.window_days || 45} Tage <PenLine className="w-3 h-3" />
              </button>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {experiments.length > 1 && (
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)} data-testid="ab-live-exp-select"
              className="bg-secondary border border-border rounded-sm px-2 py-1.5 text-xs text-foreground focus:outline-none focus:border-violet-700">
              {experiments.map(e => <option key={e.id} value={e.id}>{e.name}{e.status === 'archived' ? ' (archived)' : ''}</option>)}
            </select>
          )}
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="ab-live-indicator">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span className="text-emerald-400 font-medium uppercase tracking-wider text-[10px]">Live</span>
            <span className="font-mono">{lastUpdate ? lastUpdate.toLocaleTimeString('de-DE') : '—'}</span>
            <button onClick={() => loadLive(selectedId)} disabled={refreshing} title="Refresh now" data-testid="ab-live-refresh"
              className="text-muted-foreground hover:text-foreground transition-colors">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Completion status */}
      {live.completion && live.completion.complete && (() => {
        const sig = live.significance?.results || [];
        const rw = sig.find(r => r.metric === 'response_rate' && r.significant);
        const sw = sig.find(r => r.metric === 'scan_rate' && r.significant);
        const verdict = rw ? `Gewinner: ${rw.winner} (${rw.confidence}% Konfidenz, Response-Rate)`
          : sw ? `Gewinner: ${sw.winner} (${sw.confidence}% Konfidenz, Scan-Rate)`
          : 'Kein signifikanter Unterschied zwischen den Varianten.';
        return (
          <div className="border border-emerald-700/40 bg-emerald-950/20 rounded-sm px-4 py-3.5 flex items-start gap-3" data-testid="ab-complete-banner">
            <span className="text-lg leading-none mt-0.5">🏁</span>
            <div className="flex-1">
              <p className="text-sm text-emerald-300 font-medium">
                Test abgeschlossen — alle {live.completion.sent_total} Briefe versendet und das Antwortfenster ({live.completion.window_days} Tage) ist für jeden Brief abgelaufen.
              </p>
              <p className="text-xs text-foreground/80 mt-1" data-testid="ab-complete-verdict">{verdict}</p>
            </div>
            {exp.status === 'active' && (
              <button onClick={archiveExp} data-testid="ab-complete-archive-btn"
                className="shrink-0 text-xs px-3 py-1.5 rounded-sm bg-emerald-700 hover:bg-emerald-600 text-white transition-colors">
                Test archivieren
              </button>
            )}
          </div>
        );
      })()}
      {live.completion && !live.completion.complete && (() => {
        const c = live.completion;
        const decided = variants.reduce((a, v) => a + (v.stats?.decided || 0), 0);
        const daysLeft = c.estimated_done_at
          ? Math.max(0, Math.ceil((new Date(c.estimated_done_at + 'T23:59:59') - Date.now()) / 86400000)) : null;
        if (c.target_reached) {
          return (
            <div className="border border-amber-700/40 bg-amber-950/15 rounded-sm p-4" data-testid="ab-window-banner">
              <div className="flex items-center gap-5 flex-wrap">
                <div className="flex items-baseline gap-2 shrink-0">
                  <Clock className="w-4 h-4 text-amber-400 self-center" />
                  <span className="text-3xl font-heading text-amber-300" data-testid="ab-countdown-days">{daysLeft ?? '—'}</span>
                  <span className="text-xs text-amber-300/80">Tag{daysLeft === 1 ? '' : 'e'} bis zum Abschluss</span>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                    <span>{decided} von {c.sent_total} Briefen entschieden</span>
                    <span>{c.open_total} warte{c.open_total === 1 ? 't' : 'n'} noch auf Reaktion</span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 transition-all" style={{ width: `${c.sent_total > 0 ? Math.round((decided / c.sent_total) * 100) : 0}%` }} />
                  </div>
                </div>
                <span className="text-[11px] text-muted-foreground shrink-0" data-testid="ab-countdown-date">
                  Alle Briefe versendet ✓ — fertig ca. am <span className="text-foreground font-medium">{c.estimated_done_at ? new Date(c.estimated_done_at).toLocaleDateString('de-DE') : '—'}</span>
                  <span className="block text-[10px] text-muted-foreground/70">Antwortfenster: {c.window_days} Tage nach dem letzten Versand · antwortet jemand früher, geht es schneller</span>
                </span>
              </div>
            </div>
          );
        }
        const totalNeeded = (exp.target_n || 50) * variants.length;
        const remaining = Math.max(0, totalNeeded - c.sent_total);
        const earliest = new Date(Date.now() + c.window_days * 86400000);
        return (
          <div className="border border-border bg-card rounded-sm px-4 py-3 flex items-center gap-2.5 flex-wrap" data-testid="ab-sending-banner">
            <Send className="w-4 h-4 text-sky-400 shrink-0" />
            <span className="text-xs text-muted-foreground">
              Noch <span className="text-foreground font-medium">{remaining} Brief{remaining === 1 ? '' : 'e'}</span> zu versenden ({c.sent_total}/{totalNeeded}).
              Nach dem letzten Versand läuft das Antwortfenster von {c.window_days} Tagen —
              gingen die restlichen Briefe heute raus, wäre der Test frühestens am <span className="text-foreground font-medium">{earliest.toLocaleDateString('de-DE')}</span> abgeschlossen.
            </span>
          </div>
        );
      })()}

      {/* Variant cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${variants.length >= 3 ? 'lg:grid-cols-3' : ''} gap-4`}>
        {variants.map(v => <VariantCard key={v.id} variant={v} targetN={exp.target_n || 50} leaders={leaders} />)}
      </div>

      {/* Evaluation + timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SignificancePanel sig={live.significance} variants={variants} />
        <EngagementSigPanel es={live.engagement_significance} />
        <HeadlineSplitPanel hs={live.headline_split} />
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-4" data-testid="ab-live-timeline">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-foreground">Cumulative {metric === 'scans' ? 'QR scans' : 'responses'} per variant</h2>
            <div className="flex rounded-sm border border-border overflow-hidden">
              {['scans', 'responses'].map(m => (
                <button key={m} onClick={() => setMetric(m)} data-testid={`ab-live-metric-${m}`}
                  className={`px-3 py-1 text-[11px] transition-colors ${metric === m ? 'bg-violet-700 text-white' : 'bg-secondary/40 text-muted-foreground hover:text-foreground'}`}>
                  {m === 'scans' ? 'Scans' : 'Responses'}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={chartData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#64748b' }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: '#64748b' }} allowDecimals={false} axisLine={false} tickLine={false} />
              <RTooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 2, fontSize: 11 }} labelStyle={{ color: '#94a3b8' }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {(() => { const colors = variantColors(variants); return variants.map(v => (
                <Line key={v.id} type="monotone" dataKey={`${prefix}${v.id}`} name={v.name}
                  stroke={colors[v.id]} strokeWidth={2} dot={false} />
              )); })()}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>


      {/* Live ticker */}
      <LiveTicker events={live.events} isAdmin={isAdmin} onExcludeScan={handleExcludeScan} />

      {/* Experiments & setup (moved here from Outreach Statistics) */}
      <AbTestsSection experiments={experiments} unassigned={unassigned} reload={reloadAll} />

      <button onClick={() => navigate('/outreach-stats')} data-testid="ab-live-to-stats"
        className="text-[11px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
        Full outreach statistics <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
